import { useState } from "react";
import { updateHub } from "../../../services/hubService";
import { CreditCard, Pencil, Save, X, Loader } from "lucide-react";
import BankingDetailsForm, {
  BankingDetailsView,
} from "../hub/BankingDetailsForm";

const BankingDetailsSection = ({ hub, onUpdated }) => {
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [showAccount, setShowAccount] = useState(false);
  const [fields, setFields] = useState({
    bankAccount_BankName: hub?.bankingDetail?.bankName ?? "",
    bankAccount_BranchCode: hub?.bankingDetail?.branchCode ?? "",
    bankAccount_AccountNumber: hub?.bankingDetail?.accountNumber ?? "",
  });

  const handleChange = (key, value) =>
    setFields((prev) => ({ ...prev, [key]: value }));

  const handleCancel = () => {
    setFields({
      bankAccount_BankName: hub?.bankingDetail?.bankName ?? "",
      bankAccount_BranchCode: hub?.bankingDetail?.branchCode ?? "",
      bankAccount_AccountNumber: hub?.bankingDetail?.accountNumber ?? "",
    });
    setEditMode(false);
    setError("");
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      const formData = new FormData();
      // Hub required fields — keep existing values
      formData.append("Id", hub.id);
      formData.append("Name", hub.name);
      formData.append("Bio", hub.bio);
      formData.append("UserId", hub.userId);
      formData.append("ShowAddress", hub.showAddress ?? false);
      formData.append("AddressId", hub.addressId ?? hub.address?.id ?? "");
      formData.append("Address_DisplayName", hub.address?.displayName ?? "");
      formData.append("Address_FullAddress", hub.address?.fullAddress ?? "");
      formData.append("Address_City", hub.address?.city ?? "");
      formData.append("Address_Province", hub.address?.province ?? "");
      formData.append("Address_PostalCode", hub.address?.postalCode ?? "");
      formData.append("Address_Latitude", hub.address?.latitude ?? 0);
      formData.append("Address_Longitude", hub.address?.longitude ?? 0);
      // Updated banking
      formData.append("BankAccount_Id", hub.bankingDetail?.id ?? "");
      formData.append("BankAccount_HubId", hub.id);
      formData.append("BankAccount_Name", hub.bankingDetail?.name ?? hub.name);
      formData.append("BankAccount_BankName", fields.bankAccount_BankName);
      formData.append(
        "BankAccount_AccountNumber",
        fields.bankAccount_AccountNumber,
      );
      formData.append("BankAccount_BranchCode", fields.bankAccount_BranchCode);

      const res = await updateHub(hub.id, formData);
      if (res.isSuccess) {
        setEditMode(false);
        onUpdated?.();
      } else {
        setError(res.message ?? "Failed to update banking details.");
      }
    } catch (err) {
      setError(err.response?.data?.message ?? "Something went wrong.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-bg-surface border border-border rounded-2xl p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <CreditCard size={15} className="text-primary" />
          <h3 className="text-sm font-semibold text-text-primary">
            Banking Details
          </h3>
        </div>
        {editMode ? (
          <div className="flex items-center gap-2">
            <button
              onClick={handleCancel}
              className="flex items-center gap-1 px-3 py-1.5 border border-border rounded-xl text-xs text-text-muted hover:border-border-hover transition"
            >
              <X size={12} /> Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1 px-3 py-1.5 ingroove-gradient-bg text-white text-xs font-semibold rounded-xl hover:opacity-90 transition disabled:opacity-50"
            >
              {saving ? (
                <>
                  <Loader size={12} className="animate-spin" /> Saving...
                </>
              ) : (
                <>
                  <Save size={12} /> Save
                </>
              )}
            </button>
          </div>
        ) : (
          <button
            onClick={() => setEditMode(true)}
            className="flex items-center gap-1 px-3 py-1.5 border border-border rounded-xl text-xs text-text-muted hover:border-primary hover:text-primary transition"
          >
            <Pencil size={12} /> Edit
          </button>
        )}
      </div>

      {error && (
        <p className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2 mb-4">
          {error}
        </p>
      )}

      {editMode ? (
        <BankingDetailsForm fields={fields} onChange={handleChange} />
      ) : (
        <BankingDetailsView
          bankingDetail={hub?.bankingDetail}
          maskedBankAccount={hub?.maskedBankAccount}
          showAccount={showAccount}
          onToggleAccount={() => setShowAccount((p) => !p)}
        />
      )}
    </div>
  );
};

export default BankingDetailsSection;
